import { Directive, ElementRef, NgZone, OnDestroy, AfterViewInit, Input } from '@angular/core';

@Directive({
  selector: '[scrollActivity]',
  standalone: true,
})
export class ScrollActivityDirective implements AfterViewInit, OnDestroy {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private el: HTMLElement;
  // Scroll element may become an inner wrapper after refactor.
  private scrollEl: HTMLElement;
  private trackEl: HTMLElement | null = null;
  private thumbEl: HTMLElement | null = null;
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private resizeObserver: ResizeObserver | null = null;
  // Hover/drag state used to decide whether the track should stay visible.
  private isHovering = false;
  private isDragging = false;
  private dragStartY = 0;
  private dragStartScrollTop = 0;

  @Input() scrollActivityCustom: boolean = false; // enable custom overlay track/thumb
  private handler = () => {
    // Mark as actively scrolling.
    this.el.setAttribute('data-scrolling', 'true');
    if (this.timeoutId) clearTimeout(this.timeoutId);
    // Remove after short idle period.
    this.timeoutId = setTimeout(() => {
      this.el.removeAttribute('data-scrolling');
    }, 300);

    if (this.scrollActivityCustom) {
      this.updateThumb();
      this.showOverlayTrack();
    }
  };

  constructor(
    private ref: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {
    this.el = ref.nativeElement;
    this.scrollEl = this.el; // default before overlay refactor
  }

  ngAfterViewInit() {
    // Use passive listener for performance.
    this.zone.runOutsideAngular(() => {
      // For custom overlay we must create inner scroll element first, then attach listener to it.
      if (this.scrollActivityCustom) {
        this.initOverlay();
      }
      this.scrollEl.addEventListener('scroll', this.handler, { passive: true });
      if (this.scrollActivityCustom) {
        this.el.addEventListener('mouseenter', this.onMouseEnter, { passive: true });
        this.el.addEventListener('mouseleave', this.onMouseLeave, { passive: true });
      }
    });
  }

  ngOnDestroy() {
    this.scrollEl.removeEventListener('scroll', this.handler as EventListener);
    if (this.scrollActivityCustom) {
      this.el.removeEventListener('mouseenter', this.onMouseEnter as EventListener);
      this.el.removeEventListener('mouseleave', this.onMouseLeave as EventListener);
      this.thumbEl?.removeEventListener('mousedown', this.onThumbMouseDown as EventListener);
    }
    this.stopDragging();
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private initOverlay() {
    this.el.classList.add('overlay-scroll--custom');

    const originalChildren = Array.from(this.el.childNodes).filter(
      (n) => n.nodeType === Node.ELEMENT_NODE,
    ) as HTMLElement[];
    const inner = document.createElement('div');
    inner.className = 'overlay-scroll--inner';
    inner.style.height = '100%';
    inner.style.overflow = 'auto';
    inner.style.position = 'relative';
    this.el.style.overflow = 'hidden';
    originalChildren.forEach((c) => inner.appendChild(c));
    this.el.appendChild(inner);
    this.scrollEl = inner;

    this.trackEl = document.createElement('div');
    this.trackEl.className = 'overlay-scroll--track';
    this.thumbEl = document.createElement('div');
    this.thumbEl.className = 'overlay-scroll--thumb';
    this.thumbEl.addEventListener('mousedown', this.onThumbMouseDown);
    this.trackEl.appendChild(this.thumbEl);
    this.el.appendChild(this.trackEl);
    this.updateThumb();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateThumb());
      this.resizeObserver.observe(this.scrollEl);
      originalChildren.forEach((child) => {
        if (child instanceof HTMLElement) {
          this.resizeObserver?.observe(child);
        }
      });
    }
  }

  private updateThumb() {
    if (!this.thumbEl) return;
    const scrollHeight = this.scrollEl.scrollHeight;
    const clientHeight = this.scrollEl.clientHeight;
    if (scrollHeight <= clientHeight) {
      this.trackEl?.style.setProperty('display', 'none');
      return;
    }
    this.trackEl?.style.setProperty('display', '');
    const ratio = clientHeight / scrollHeight;
    const thumbHeight = Math.max(24, clientHeight * ratio);
    const scrollTop = this.scrollEl.scrollTop;
    const maxScroll = scrollHeight - clientHeight;
    const trackHeight = this.trackEl ? this.trackEl.clientHeight : clientHeight;
    const available = trackHeight - thumbHeight;
    const top = available <= 0 ? 0 : (scrollTop / maxScroll) * available;
    this.thumbEl.style.height = `${thumbHeight}px`;
    this.thumbEl.style.transform = `translate3d(0, ${top}px, 0)`;
    this.thumbEl.setAttribute('data-thumb-top', String(Math.round(top)));
  }

  private showOverlayTrack = () => {
    if (!this.trackEl) return;
    this.trackEl.style.opacity = '1';
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
  };

  private scheduleHideOverlay = () => {
    if (!this.trackEl) return;
    if (this.hideTimeoutId) clearTimeout(this.hideTimeoutId);
    // Never hide the track while the user is actively dragging the thumb.
    if (this.isDragging) return;
    this.hideTimeoutId = setTimeout(() => {
      this.trackEl && (this.trackEl.style.opacity = '0');
    }, 500);
  };

  private onMouseEnter = () => {
    this.isHovering = true;
    this.showOverlayTrack();
  };

  private onMouseLeave = () => {
    this.isHovering = false;
    this.scheduleHideOverlay();
  };

  private onThumbMouseDown = (event: MouseEvent) => {
    // Only respond to primary button drags.
    if (event.button !== 0) return;
    event.preventDefault();
    this.isDragging = true;
    this.dragStartY = event.clientY;
    this.dragStartScrollTop = this.scrollEl.scrollTop;
    this.thumbEl?.classList.add('is-dragging');
    document.body.style.userSelect = 'none';
    this.showOverlayTrack();
    document.addEventListener('mousemove', this.onDocumentMouseMove);
    document.addEventListener('mouseup', this.onDocumentMouseUp);
  };

  private onDocumentMouseMove = (event: MouseEvent) => {
    if (!this.isDragging || !this.trackEl || !this.thumbEl) return;
    const trackHeight = this.trackEl.clientHeight;
    const thumbHeight = this.thumbEl.clientHeight;
    const available = trackHeight - thumbHeight;
    if (available <= 0) return;
    const scrollHeight = this.scrollEl.scrollHeight;
    const clientHeight = this.scrollEl.clientHeight;
    const maxScroll = scrollHeight - clientHeight;
    const deltaY = event.clientY - this.dragStartY;
    const scrollDelta = (deltaY / available) * maxScroll;
    const newScrollTop = Math.min(maxScroll, Math.max(0, this.dragStartScrollTop + scrollDelta));
    // Triggers the native 'scroll' event, which updates the thumb and keeps the track visible.
    this.scrollEl.scrollTop = newScrollTop;
  };

  private onDocumentMouseUp = () => {
    if (!this.isDragging) return;
    this.stopDragging();
    // If the pointer ended up outside the host, resume the normal hide timer.
    if (!this.isHovering) this.scheduleHideOverlay();
  };

  private stopDragging() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.thumbEl?.classList.remove('is-dragging');
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', this.onDocumentMouseMove);
    document.removeEventListener('mouseup', this.onDocumentMouseUp);
  }
}
